import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { IApplication } from "@rentiohq/shared-frontend/dist/types/application.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import React from "react";
import { InfoIndicator } from "../../ApplicationsRentmatch/components/ApplicationTable/applicationIndicators";
import { OtherApplicationsModal } from "../../ApplicationsRentmatch/components/OtherApplicationsModal";
import * as S from "../VisitMode.styled";
import { HistoryIcon, HomeIcon, NoteIcon, ThumbDownIcon } from "./VisitIcons";

interface IProps {
  application: IApplication;
}

/**
 * The count in a word that agrees with it, "1 note" or "3 notes", for the
 * count's tooltip. Both keys are passed whole, so the i18n key check can still
 * find them.
 */
const getCountText = (count: number, oneKey: string, manyKey: string) =>
  getLocalizedText(count === 1 ? oneKey : manyKey, { count: `${count}` });

/**
 * What this visitor did at the office's other properties (DEV-3542): a count of
 * no-shows, of notes and of properties, each a mark and a number like the facts
 * beside it, and a bordered history button that opens the details.
 *
 * Against the right edge, apart from this property's own facts, because
 * without that the counts read as more facts about this candidacy. The button carries only its mark: on a phone, which is all this
 * screen is for, a word does not fit on the line.
 *
 * A count with nothing to count is left out, and without other candidacies
 * there is nothing to open, so nothing is drawn at all.
 *
 * The counts are read off the candidacy the screen already holds. api-next
 * attaches them to every candidacy in the list, scoped to the broker's own
 * office, so nothing here can show another office's data and opening a card
 * costs no request. Only the dialog asks for more.
 */
export const VisitHistory = (props: IProps) => {
  const { application } = props;

  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const meta = application.otherApplicationsMeta;
  const noShows = meta?.noShows ?? [];
  const notes = meta?.notes ?? [];
  const properties = meta?.properties ?? [];
  // Counts this candidacy too, which is why the list says "candidate for N
  // properties" only from two on. Same field and same threshold as there.
  const propertyCount = application.otherApplicationsTotalCount ?? 0;

  if (properties.length === 0) {
    return null;
  }

  const counts = [
    noShows.length > 0 && {
      key: "noShows",
      icon: <ThumbDownIcon />,
      value: noShows.length,
      label: getCountText(
        noShows.length,
        "visit_mode.card.history.no_shows.one",
        "visit_mode.card.history.no_shows.many",
      ),
    },
    notes.length > 0 && {
      key: "notes",
      icon: <NoteIcon />,
      value: notes.length,
      label: getCountText(
        notes.length,
        "visit_mode.card.history.notes.one",
        "visit_mode.card.history.notes.many",
      ),
    },
    propertyCount > 1 && {
      key: "properties",
      icon: <HomeIcon />,
      value: propertyCount,
      label: getLocalizedText("visit_mode.card.history.properties.many", {
        count: `${propertyCount}`,
      }),
    },
  ].filter(Boolean) as {
    key: string;
    icon: React.ReactNode;
    value: number;
    label: string;
  }[];

  return (
    <S.HistorySide>
      {counts.map(count => (
        <InfoIndicator
          key={count.key}
          icon={count.icon}
          value={count.value}
          tooltip={count.label}
        />
      ))}
      <S.HistoryButton
        type="button"
        onClick={() => setIsModalOpen(true)}
        title={getLocalizedText("system.history")}
        aria-label={getLocalizedText("system.history")}
      >
        <HistoryIcon size={20} />
      </S.HistoryButton>

      {/* The candidate list's own dialog: a table on a wide screen, a card per
          candidacy on a phone, so both screens show the same thing. */}
      {isModalOpen && (
        <OtherApplicationsModal
          candidateName={getName(application.contacts?.[0] || {})}
          properties={properties}
          notes={notes}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </S.HistorySide>
  );
};
